import type { ReactNode } from 'react';
import { ltr, num } from '../format';

/**
 * The workstation: the HUD, the brief rail, the objectives, the file tree,
 * the editor, the git graph and the reflog (components/play/*).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## The shape
 *
 * One group per component — `hud`, `files`, `editor`, `pulls`, `work`,
 * `frame`, `graph`, `commit`, `reflog`, `objectives`, `brief` — and Play.tsx's
 * own strings at the top level, because Play is the screen these groups hang
 * off rather than one more panel on it.
 *
 * ## Git words, and which half of the sentence they live in
 *
 * docs/i18n.md §5: a noun the learner types or reads printed stays in Latin —
 * `HEAD`, `origin`, `main`, `commit`, `rebase`, `stash`, `reflog`. The prose
 * around it is ordinary Arabic — branch is فرع, merge is دمج, conflict is
 * تعارض, staged is مجهَّز, untracked is غير متتبَّع, clean is نظيف. So a chip
 * reads `HEAD منفصل عند a1b2c3d` and a detail row is keyed `الموضوع` while
 * the one beside it is still keyed `commit`.
 *
 * Machine strings that land inside an Arabic sentence — a path, a hash, a
 * branch name — have to be bidi-isolated or `src/cart.js` arrives as
 * `cart.js/src`. That is done at the call site, with `ltr()` from lib/format
 * for a plain string and `<Machine>` from web/src/ui for markup, and
 * deliberately not here: lib/format reaches lib/locale, which paints `<html>`
 * as it loads, and this directory is meant to stay importable by a plain
 * `node --test` with no DOM under it.
 */

/* ── Arabic agreement ─────────────────────────────────────────────────────── */

interface Forms {
  /** One. Arabic names the thing and drops the numeral: `ملف متعارض`. */
  one: string;
  /** Two. The dual, not `2` plus a plural: `ملفان متعارضان`. */
  two: string;
  /** 3–10, where the noun is a broken plural in the genitive. */
  few: string;
  /** 11–99, where it goes back to an accusative singular. */
  many: string;
  /** 0, 100, 101 … — the nominative singular again. */
  other: string;
}

/**
 * Pick the Arabic form a count agrees with.
 *
 * CLDR gives Arabic six categories to English's two, and five of them are in
 * play for any count a learner can reach here. A sentence that appended a
 * letter the way English pluralises would be wrong for every value except
 * the handful it happened to be written against, which is why nothing in this
 * file builds a count into a phrase at the call site.
 */
function plural(n: number, forms: Forms): string {
  const rest = n % 100;
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  if (rest >= 3 && rest <= 10) return forms.few;
  if (rest >= 11 && rest <= 99) return forms.many;
  return forms.other;
}

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  /* --- The screen (Play.tsx) --------------------------------------------- */

  /** The graph pane, on a level that does not expect a repository. */
  noRepoYet: 'Nothing to draw yet. This fills in the moment a repository exists here.',
  /** Checking a level that was already passed. */
  stillClear: 'Still clear.',
  checkingWork: 'Checking your work…',
  checkTakingLonger: 'This check is taking longer than usual. It will stop if a command times out; your work is kept.',
  checkIncomplete: 'The check timed out before reaching a verdict. Review any command that keeps running, then retry. Your work is kept.',
  retryCheck: 'Retry check',
  objectivesToGo: (n: number) =>
    `${n} objective${n === 1 ? '' : 's'} to go. Each one says what is wrong, not how to fix it.`,
  confirmLastHint: 'This one gives the answer. Show it anyway?',
  confirmSkip:
    'Leave this level for now? It stays on the map with no stars, and you can come back to it whenever you like.',
  confirmReset: 'Rebuild the workspace from the start of the level? Your changes here are lost.',
  workspaceReset: 'Workspace reset.',
  workRestored: 'Your work on this level is back where you left it. Reset starts it fresh.',
  workLevelChanged: 'This level was updated since you last worked on it, so it starts fresh.',
  levelLabel: (level: number) => `Level ${level}`,
  couldNotOpen: 'This level could not be opened.',
  tryAgain: 'Try again',
  allLevels: 'All levels',
  preparing: 'Preparing your workspace.',
  /**
   * The wait while a level opens, narrated the way the boot screen narrates
   * the machine starting. Same four-step vocabulary, same marks; the steps
   * are the real phases the runtime reports, and `starting` is drawn only on
   * the levels that have something to start.
   */
  opening: {
    barLabel: 'Opening the level',
    steps: {
      clearing: 'Clearing the workspace',
      unpacking: 'Unpacking the level',
      starting: 'Starting what the level needs running',
      reading: 'Reading the repository',
    },
  },
  help: 'Help',
  /** A pass the grader could not vouch for. Each language joins its own list. */
  unverified: (reasons: string[]) =>
    `Cleared, but this looked implausible: ${reasons.join('; ')}. Replay the level to verify it.`,
  stillNotPassing:
    'Still not passing? Leave it for now — it keeps its place on the map, and nothing stops you coming back.',
  skipLevel: 'Skip this level',
  whatJustHappened: 'What just happened',
  readDebrief: 'Read the full debrief',
  nextWorld: (world: string) => `Next · ${world}`,
  straightToLevel: (level: number) => `Straight to level ${level}`,
  nextLevel: (level: number) => `Next · Level ${level}`,

  /* --- Hud.tsx ------------------------------------------------------------ */

  hud: {
    brandTitle: 'Back to the level list',
    noRepo: 'no repository',
    noRepoYet: 'no repository yet',
    detachedAt: (hash: string) => `detached at ${hash}`,
    unbornBranch: 'unborn branch',
    clean: 'clean',
    conflicted: (n: number) => `${n} conflicted`,
    /**
     * The whole working-tree phrase, not the fragments.
     *
     * It used to be three `${n} word` pieces joined with ` · ` in the
     * component, which fixes the order and the separator for every language
     * at once and hands a translator three half-sentences. Each language
     * builds the finished chip instead, so Arabic can put the count where the
     * count goes and agree the noun with it.
     */
    treeStatus: (staged: number, modified: number, untracked: number) => {
      const bits: string[] = [];
      if (staged) bits.push(`${staged} staged`);
      if (modified) bits.push(`${modified} modified`);
      if (untracked) bits.push(`${untracked} untracked`);
      return bits.join(' · ');
    },
    sandboxWorld: (world: string) => `Sandbox · ${world}`,
    warmup: 'Warm-up',
    warmupTitle: 'The tools this level needs, in two minutes',
    reset: 'Reset',
    resetTitle: 'Rebuild the workspace from the level checkpoint',
    checkTitle: 'Grade the repository state (Ctrl+Enter)',
    checking: 'Checking…',
    check: 'Check my work',
  },

  /* --- PlayFrame.tsx ------------------------------------------------------ */

  frame: {
    graph: 'Git graph',
    reflog: 'Reflog',
    resizeTerminal: 'Resize the terminal panel',
    resizeTicket: 'Resize the ticket panel',
  },
  /**
   * The phone layout (PlayFrame, PhoneKeys): one panel at a time behind a bar
   * of tabs, and the keys a phone's keyboard does not have.
   */
  phone: {
    tabs: 'Level panels',
    task: 'Task',
    terminal: 'Terminal',
    graph: 'Graph',
    iris: 'Iris',
    keys: 'Keys a phone keyboard lacks',
    tab: 'Tab: complete the name',
    ctrlC: 'Control-C: stop what is running',
    esc: 'Escape',
    up: 'Previous command',
    down: 'Next command',
    left: 'Cursor left',
    right: 'Cursor right',
  },

  /* --- MissionBrief.tsx --------------------------------------------------- */

  brief: {
    objectives: 'Objectives',
    documentTitle: (level: number, title: string) => `Level ${level} · ${title} · EngineerOS`,
    /**
     * Under the brief of a checkpoint in lib/checkpoint.ts's
     * MEASURED_AGAINST_A_MODEL, and nowhere else. The model is named by its
     * exact version and the day, which is what keeps the claim true after the
     * next release; `date` is on the UTC calendar.
     */
    anyTool: (model: string, modelId: string, date: string, runs: number) =>
      `Use any tool you like here, AI included: you are graded on the repository you leave. On ${date}, ${model} (${modelId}), working alone, was given this checkpoint ${num(runs, 'en')} times and did not pass it once.`,
    /**
     * Under the brief of every level that is not a checkpoint (the AI-use
     * policy, docs/ai-era-plan.md decision 4). Outside the ticket because
     * Iris is real and the ticket is Lattice's fiction. A learner who has
     * never written JavaScript meets it from level 9; this is where they
     * learn that asking is the expected move, not a shortcut.
     */
    anyToolLevel:
      'Use any tool you like, AI included: ask your agent, or any other assistant you use, what the code does. You are graded on the repository you leave, not on how you got there.',
    /**
     * The same, on a level that never uses git (lib/uses-git.ts; level 0 among them): there
     * is no repository, and a sentence about one is one more thing a newcomer
     * has to puzzle out on the first screen.
     */
    anyToolNoRepo:
      'Use any tool you like, AI included: ask your agent, or any other assistant you use, to explain what you see. You are graded on the files you leave, not on how you got there.',
  },

  /* --- Objectives.tsx ----------------------------------------------------- */

  objectives: {
    hiddenCheck: (title: string) => `Hidden check: ${title}`,
  },

  /* --- WorkPane.tsx ------------------------------------------------------- */

  work: {
    tablist: 'Work area',
    terminal: 'Terminal',
    editor: 'Editor',
    pulls: 'Pull requests',
    /** Until TerminalPane says otherwise. */
    connecting: 'connecting…',
    noFile: 'no file open',
    /** `path` arrives as a <Machine> so the path keeps its own direction. */
    fileState: (path: ReactNode, dirty: boolean): ReactNode[] => [path, ' · ', dirty ? 'unsaved' : 'saved'],
    /** The head's status line while the pull request tab is the one showing. */
    pullCount: (n: number) => (n === 0 ? 'no pull requests' : `${n} pull request${n === 1 ? '' : 's'}`),
    pullsReading: 'reading…',
    /** A toggle, shown with the editor: the terminal beside it, or the editor alone. */
    split: 'Side by side',
    splitTitle: 'Show the terminal beside the editor',
    /** The divider between the two, for a screen reader. */
    resizeSplit: 'Resize the editor and the terminal',
  },

  /* --- FileTree.tsx ------------------------------------------------------- */

  files: {
    head: 'Files',
    treeLabel: 'Workspace files',
    unsavedChanges: 'unsaved changes',
    reading: 'Reading the workspace…',
    empty: 'No files here yet. Create one in the terminal.',
    unavailable: (error: ReactNode): ReactNode[] => [
      'The file list is unavailable. ',
      error,
      '. The terminal still works, and this pane recovers on the next refresh.',
    ],
    listFailed: 'the workspace could not be listed',
    /**
     * What the root is called when the mission has no cwd.
     *
     * Latin in both languages, and not an oversight: it sits in the slot a
     * real directory name sits in, beside `src` and `storefront`, inside the
     * `dir="ltr"` run the tree keeps for names. An Arabic word there would
     * read as a directory that is not on the disk.
     */
    rootFallback: 'workspace',
  },

  /* --- EditorPane.tsx ----------------------------------------------------- */

  editor: {
    blankLabel: 'Editor',
    opening: 'Opening…',
    blankHint: 'Pick a file on the left to edit it here. Ctrl+S saves it into the workspace the terminal is standing in.',
    save: 'Save',
    saving: 'Saving…',
    saveTitle: 'Save (Ctrl+S)',
    unsaved: 'unsaved',
    saved: 'saved',
    noFile: 'no file open',
    /** A `window.confirm`: the caller isolates the two paths with `ltr()`. */
    confirmDiscard: (open: string, next: string) => `${open} has unsaved changes. Open ${next} and lose them?`,
    openFailed: 'the file could not be opened',
    saveFailed: 'the file could not be saved',
    run: 'Run',
    runTests: 'Run tests',
    /** `command` is the exact line the button types, e.g. `node src/report.js`. */
    runTitle: (command: string) => `Save, then type ${command} into the terminal (F5)`,
    runTestsTitle: (command: string) => `Save, then type ${command} into the terminal`,
    /** The shell is not at its prompt, so a typed line would go to whatever is running. */
    runBusy: 'The terminal is still running something. Stop it there with Ctrl+C, then run again.',
    runDetached: 'The terminal is not connected, so there is nowhere to run this.',
    /**
     * A Check saves the open file first, except when the file changed on disk
     * since the editor read it: then the disk is the newer work, and that is
     * what was graded. `path` arrives bare; Arabic isolates it.
     */
    checkedDisk: (path: string) =>
      `${path} was changed outside the editor, so Check graded the version on disk. What is here is still unsaved.`,
  },

  /* --- PullRequestPane.tsx ------------------------------------------------
     The pane reads `pulls/N.json` out of the bare repository (docs/github.md).
     Everything in the file itself — the title, the body, a branch name, a
     comment — was written inside the repository and stays in the language the
     repository is written in. These are the words around it. */

  pulls: {
    label: 'Pull requests',
    reading: 'Reading the repository…',
    emptyTitle: 'No pull requests',
    /** Most levels never open one, so the empty state explains rather than apologises. */
    emptyNote:
      'Most levels have none. When one is opened on the repository this workspace pushes to, it is drawn here — its checks, its commits, the files it touches and every comment left on it.',
    unavailable: (error: ReactNode): ReactNode[] => [
      'The pull requests could not be read. ',
      error,
      '. The terminal still works, and this pane recovers on the next command.',
    ],
    readFailed: 'the base repository could not be read',
    /** The three states a pull request can be in. `draft` is a flag beside them. */
    state: {
      open: 'Open',
      merged: 'Merged',
      closed: 'Closed',
    },
    draft: 'Draft',
    /** `author` arrives as a <Machine>: it is a name out of the repository. */
    openedBy: (author: ReactNode, date: string): ReactNode[] => ['opened by ', author, ' · ', date],
    /**
     * Said for a screen reader. The line itself is drawn as `base ← head`,
     * which is two branch names and an arrow and reads as neither.
     */
    mergesInto: (head: string, base: string) => `merges ${head} into ${base}`,
    checks: 'Checks',
    checkPass: 'passed',
    checkFail: 'failed',
    checkPending: 'pending',
    reviews: 'Reviews',
    reviewApproved: 'approved',
    reviewChanges: 'requested changes',
    reviewCommented: 'commented',
    commits: 'Commits',
    files: 'Files changed',
    comments: 'Comments',
    resolved: 'resolved',
    /** A comment about the branch rather than about a line of it. */
    onTheBranch: 'on the branch',
  },

  /* --- GitGraph.tsx ------------------------------------------------------- */

  graph: {
    label: 'Git commit graph',
    /** Both halves are the machine's, so both languages say the same thing. */
    commitLabel: (short: string, subject: string) => `${short} ${subject}`,
    noRepo: 'No git repository in the mission directory yet.',
    noCommits: 'Repository has no commits yet.',
    /** The one authored word among the ref pills; the rest are real refs. */
    unreachable: 'unreachable',
  },

  /* --- CommitDetail.tsx --------------------------------------------------- */

  commit: {
    /** Stays `commit`: it is the noun the learner types, not a field name. */
    keyCommit: 'commit',
    keySubject: 'subject',
    keyAuthor: 'author',
    keyParents: 'parents',
    keyRefs: 'refs',
    authorAndDate: (author: ReactNode, date: string): ReactNode[] => [author, ' · ', date],
    noParents: 'none (root commit)',
    refsReachable: 'none directly; reachable from a branch',
    refsUnreachable: 'none. No branch reaches this commit, but it still exists.',
  },

  /* --- Reflog.tsx --------------------------------------------------------- */

  reflog: {
    empty: 'No reflog entries yet.',
  },
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

/** `ثلاث مرات` — how many blind runs a model was given (docs/i18n.md §4). */
const timesAr = (n: number) =>
  n === 1 ? 'مرة واحدة' : n === 2 ? 'مرتين' : n <= 10 ? `${num(n, 'ar')} مرات` : `${num(n, 'ar')} مرة`;

export const ar: typeof en = {
  noRepoYet: 'سيظهر المخطط هنا بعد إنشاء المستودع.',
  stillClear: 'ما زال المستوى منجزًا.',
  checkingWork: 'جارٍ فحص عملك…',
  checkTakingLonger: 'يستغرق الفحص وقتًا أطول من المعتاد. سيتوقّف إذا تجاوز أمرٌ المهلة؛ وعملك محفوظ.',
  checkIncomplete: 'انتهت مهلة الفحص قبل الوصول إلى نتيجة. راجع أي أمر يظلّ يعمل، ثم أعد المحاولة. عملك محفوظ.',
  retryCheck: 'أعِد الفحص',
  objectivesToGo: (n: number) =>
    `${plural(n, {
      one: 'بقي هدف واحد',
      two: 'بقي هدفان',
      few: `بقيت ${n} أهداف`,
      many: `بقي ${n} هدفًا`,
      other: `بقي ${n} هدف`,
    })}. يوضح كل هدف موضع الخطأ، من دون أن يكشف طريقة إصلاحه.`,
  confirmLastHint: 'هذا التلميح يكشف الحل. هل تريد عرضه؟',
  confirmSkip: 'هل تريد تخطي هذا المستوى الآن؟ سيبقى على الخريطة بلا نجوم، ويمكنك العودة إليه في أي وقت.',
  confirmReset: 'هل تريد إعادة مساحة العمل إلى بداية المستوى؟ ستفقد تغييراتك الحالية.',
  workspaceReset: 'عادت مساحة العمل إلى نقطة البداية.',
  workRestored: 'عاد عملك في هذا المستوى كما تركته. «إعادة الضبط» تبدؤه من جديد.',
  workLevelChanged: 'تغيّر هذا المستوى منذ آخر مرة عملت عليه، لذلك يبدأ من جديد.',
  levelLabel: (level: number) => `المستوى ${level}`,
  couldNotOpen: 'تعذّر فتح هذا المستوى.',
  tryAgain: 'حاول مرة أخرى',
  allLevels: 'كل المستويات',
  preparing: 'جارٍ تجهيز مساحة العمل.',
  opening: {
    barLabel: 'جارٍ فتح المستوى',
    steps: {
      clearing: 'إفراغ مساحة العمل',
      unpacking: 'فكّ ضغط ملفات المستوى',
      starting: 'تشغيل ما يحتاجه المستوى',
      reading: 'قراءة المستودع',
    },
  },
  help: 'المساعدة',
  unverified: (reasons: string[]) =>
    `اجتزت المستوى، لكن تعذّر تأكيد النتيجة: ${reasons.join('؛ ')}. أعد المستوى لتأكيدها.`,
  stillNotPassing: 'ما زلت عالقًا؟ يمكنك ترك المستوى الآن والعودة إليه لاحقًا؛ سيبقى في مكانه على الخريطة.',
  skipLevel: 'تخطَّ هذا المستوى',
  whatJustHappened: 'ما الذي جرى',
  readDebrief: 'اقرأ الخلاصة كاملة',
  nextWorld: (world: string) => `التالي · ${world}`,
  straightToLevel: (level: number) => `مباشرة إلى المستوى ${level}`,
  nextLevel: (level: number) => `التالي · المستوى ${level}`,

  hud: {
    brandTitle: 'العودة إلى قائمة المستويات',
    noRepo: 'من دون مستودع',
    noRepoYet: 'لم يُنشأ المستودع بعد',
    // `HEAD` is typed and printed; `منفصل` is prose. The hash arrives already
    // isolated, so bidi cannot walk it out of the phrase.
    detachedAt: (hash: string) => `HEAD منفصل عند ${hash}`,
    unbornBranch: `فرع بلا ${ltr('commits')} بعد`,
    clean: 'نظيف',
    conflicted: (n: number) =>
      plural(n, {
        one: 'ملف متعارض',
        two: 'ملفان متعارضان',
        few: `${n} ملفات متعارضة`,
        many: `${n} ملفًا متعارضًا`,
        other: `${n} ملف متعارض`,
      }),
    treeStatus: (staged: number, modified: number, untracked: number) => {
      const bits: string[] = [];
      if (staged)
        bits.push(
          plural(staged, {
            one: 'ملف مجهَّز',
            two: 'ملفان مجهَّزان',
            few: `${staged} ملفات مجهَّزة`,
            many: `${staged} ملفًا مجهَّزًا`,
            other: `${staged} ملف مجهَّز`,
          }),
        );
      if (modified)
        bits.push(
          plural(modified, {
            one: 'ملف معدَّل',
            two: 'ملفان معدَّلان',
            few: `${modified} ملفات معدَّلة`,
            many: `${modified} ملفًا معدَّلًا`,
            other: `${modified} ملف معدَّل`,
          }),
        );
      if (untracked)
        bits.push(
          plural(untracked, {
            one: 'ملف غير متتبَّع',
            two: 'ملفان غير متتبَّعين',
            few: `${untracked} ملفات غير متتبَّعة`,
            many: `${untracked} ملفًا غير متتبَّع`,
            other: `${untracked} ملف غير متتبَّع`,
          }),
        );
      return bits.join(' · ');
    },
    sandboxWorld: (world: string) => `مساحة التجربة · ${world}`,
    warmup: 'تمرين سريع',
    warmupTitle: 'تعرّف على أدوات هذا المستوى في دقيقتين',
    reset: 'إعادة الضبط',
    resetTitle: 'أعد مساحة العمل إلى نقطة بداية المستوى',
    checkTitle: 'تحقق من حالة المستودع (Ctrl+Enter)',
    checking: 'جارٍ التحقق…',
    check: 'تحقّق من عملي',
  },

  frame: {
    graph: 'مخطط Git',
    reflog: 'reflog',
    resizeTerminal: `غيّر حجم لوحة الـ ${ltr('terminal')}`,
    resizeTicket: 'غيّر حجم لوحة المهمة',
  },
  phone: {
    tabs: 'لوحات المستوى',
    task: 'المهمة',
    terminal: ltr('Terminal'),
    graph: 'المخطط',
    iris: 'Iris',
    keys: 'مفاتيح لا تجدها في لوحة مفاتيح الهاتف',
    tab: 'Tab: أكمل الاسم',
    ctrlC: 'Control-C: أوقف ما يعمل الآن',
    esc: 'مفتاح Esc',
    up: 'الأمر السابق',
    down: 'الأمر التالي',
    left: 'المؤشر إلى اليسار',
    right: 'المؤشر إلى اليمين',
  },

  brief: {
    objectives: 'الأهداف',
    documentTitle: (level: number, title: string) => `المستوى ${level} · ${title} · EngineerOS`,
    anyTool: (model, modelId, date, runs) =>
      `استخدم هنا أي أداة تريدها، بما فيها الذكاء الاصطناعي: التقييم على المستودع الذي تتركه. في ${date} أُعطيت هذه المحطة للنموذج ${ltr(model)} (${ltr(modelId)}) وهو يعمل وحده ${timesAr(runs)}، فلم يجتزها في أيٍّ منها.`,
    anyToolLevel:
      'استخدم أي أداة تريدها، بما فيها الذكاء الاصطناعي: اسأل وكيلك، أو أي مساعد آخر تستخدمه، عمّا تفعله الشيفرة. التقييم على المستودع الذي تتركه، لا على الطريق الذي أوصلك إليه.',
    anyToolNoRepo:
      'استخدم أي أداة تريدها، بما فيها الذكاء الاصطناعي: اطلب من وكيلك، أو من أي مساعد آخر تستخدمه، أن يشرح لك ما تراه. التقييم على الملفات التي تتركها، لا على الطريق الذي أوصلك إليه.',
  },

  objectives: {
    hiddenCheck: (title: string) => `فحص خفي: ${title}`,
  },

  work: {
    tablist: 'منطقة العمل',
    terminal: ltr('Terminal'),
    editor: ltr('Editor'),
    pulls: ltr('pull requests'),
    connecting: 'جارٍ الاتصال…',
    noFile: 'لا ملف مفتوح',
    fileState: (path: ReactNode, dirty: boolean): ReactNode[] => [path, ' · ', dirty ? 'غير محفوظ' : 'محفوظ'],
    pullCount: (n: number) =>
      n === 0 ? `لا توجد ${ltr('pull requests')}` : ltr(`${n} pull request${n === 1 ? '' : 's'}`),
    pullsReading: 'جارٍ القراءة…',
    split: 'جنبًا إلى جنب',
    splitTitle: `اعرض الـ ${ltr('terminal')} بجانب الـ ${ltr('editor')}`,
    resizeSplit: `غيّر المساحة بين الـ ${ltr('editor')} والـ ${ltr('terminal')}`,
  },

  files: {
    head: 'الملفات',
    treeLabel: 'ملفات مساحة العمل',
    unsavedChanges: 'تغييرات غير محفوظة',
    reading: 'جارٍ قراءة مساحة العمل…',
    empty: `لا ملفات هنا بعد. أنشئ واحدًا في الـ ${ltr('terminal')}.`,
    unavailable: (error: ReactNode): ReactNode[] => [
      'قائمة الملفات غير متاحة. ',
      error,
      `. الـ ${ltr('terminal')} ما زال يعمل، وهذه اللوحة تتعافى مع التحديث التالي.`,
    ],
    listFailed: 'تعذّر سرد مساحة العمل',
    rootFallback: 'workspace',
  },

  editor: {
    blankLabel: ltr('Editor'),
    opening: 'جارٍ الفتح…',
    // "on the left" is a direction, and the tree is on the right in Arabic.
    // The sentence names the thing instead of pointing at a side.
    blankHint: `اختر ملفًا من الشجرة لتحريره هنا. Ctrl+S يحفظه في مساحة العمل نفسها التي يقف فيها الـ ${ltr('terminal')}.`,
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saveTitle: 'احفظ (Ctrl+S)',
    unsaved: 'غير محفوظ',
    saved: 'محفوظ',
    noFile: 'لا ملف مفتوح',
    confirmDiscard: (open: string, next: string) => `يحتوي ${open} على تغييرات غير محفوظة. هل تريد فتح ${next} وفقدها؟`,
    openFailed: 'تعذّر فتح الملف',
    saveFailed: 'تعذّر حفظ الملف',
    run: 'تشغيل',
    runTests: 'تشغيل الاختبارات',
    runTitle: (command: string) => `احفظ، ثم اكتب ${ltr(command)} في الـ ${ltr('terminal')} (${ltr('F5')})`,
    runTestsTitle: (command: string) => `احفظ، ثم اكتب ${ltr(command)} في الـ ${ltr('terminal')}`,
    runBusy: `ما زال الـ ${ltr('terminal')} يشغّل شيئًا. أوقفه هناك بالضغط على ${ltr('Ctrl+C')}، ثم شغّل من جديد.`,
    runDetached: `الـ ${ltr('terminal')} غير متصل، فلا يوجد مكان لتشغيل هذا.`,
    checkedDisk: (path: string) =>
      `تغيّر ${ltr(path)} من خارج الـ ${ltr('editor')}، لذلك فحص «تحقّق من عملي» النسخة المحفوظة على القرص. ما تراه هنا ما زال غير محفوظ.`,
  },

  pulls: {
    label: ltr('pull requests'),
    reading: 'جارٍ قراءة المستودع…',
    emptyTitle: `لا توجد ${ltr('pull requests')}`,
    emptyNote:
      `معظم المستويات لا تنشئ ${ltr('pull request')}. وعندما تنشئ واحدًا في المستودع الذي تعمل عليه، يظهر هنا مع الفحوص و ${ltr('commits')} والملفات وكل تعليق.`,
    unavailable: (error: ReactNode): ReactNode[] => [
      'تعذّرت قراءة ',
      ltr('pull requests'),
      '. ',
      error,
      `. الـ ${ltr('terminal')} ما زال يعمل، وهذه اللوحة تتعافى مع الأمر التالي.`,
    ],
    readFailed: 'تعذّرت قراءة المستودع الأصل',
    state: {
      open: 'مفتوح',
      merged: 'مدموج',
      closed: 'مغلق',
    },
    draft: 'مسودة',
    openedBy: (author: ReactNode, date: string): ReactNode[] => ['فتحه ', author, ' · ', date],
    mergesInto: (head: string, base: string) => `يدمج ${head} في ${base}`,
    checks: 'الفحوص',
    checkPass: 'نجح',
    checkFail: 'فشل',
    checkPending: 'قيد التنفيذ',
    reviews: 'المراجعات',
    reviewApproved: 'وافق',
    reviewChanges: 'طلب تغييرات',
    reviewCommented: 'علّق',
    commits: ltr('commits'),
    files: 'الملفات المتغيّرة',
    comments: 'التعليقات',
    resolved: 'محلول',
    onTheBranch: 'على الفرع',
  },

  graph: {
    label: `مخطط ${ltr('commits')} في Git`,
    commitLabel: (short: string, subject: string) => `${short} ${subject}`,
    noRepo: 'لا مستودع Git في مجلد المهمة بعد.',
    noCommits: `لا يحتوي المستودع على أي ${ltr('commit')} بعد.`,
    unreachable: 'لا يصل إليه أي فرع',
  },

  commit: {
    keyCommit: 'commit',
    keySubject: 'الموضوع',
    keyAuthor: 'المؤلف',
    keyParents: 'الآباء',
    keyRefs: 'المراجع',
    authorAndDate: (author: ReactNode, date: string): ReactNode[] => [author, ' · ', date],
    noParents: 'لا يوجد (أول commit)',
    refsReachable: 'لا يوجد مباشرة؛ يصل إليه أحد الفروع',
    refsUnreachable: `لا يوجد. لا يصل أي فرع إلى هذا ${ltr('commit')}، لكنه ما زال موجودًا.`,
  },

  reflog: {
    empty: 'لا يحتوي reflog على أي إدخالات بعد.',
  },
};

export const de: typeof en = {
  noRepoYet: 'Noch nichts zu zeichnen. Das füllt sich, sobald es hier ein Repository gibt.',
  stillClear: 'Immer noch geschafft.',
  checkingWork: 'Deine Arbeit wird geprüft …',
  checkTakingLonger: 'Diese Prüfung dauert länger als üblich. Bei einer Zeitüberschreitung wird sie beendet; deine Arbeit bleibt erhalten.',
  checkIncomplete: 'Die Prüfung hat das Zeitlimit erreicht, ohne zu einem Urteil zu kommen. Prüfe Befehle, die weiterlaufen, und versuche es erneut. Deine Arbeit bleibt erhalten.',
  retryCheck: 'Prüfung wiederholen',
  objectivesToGo: (n) =>
    `${n === 1 ? 'Noch ein Ziel' : `Noch ${n} Ziele`}. Jedes sagt, was falsch ist, nicht, wie man es behebt.`,
  confirmLastHint: 'Dieser Hinweis verrät die Lösung. Trotzdem anzeigen?',
  confirmSkip: 'Dieses Level vorerst liegen lassen? Es bleibt ohne Sterne auf der Karte, und du kannst jederzeit zurückkommen.',
  confirmReset: 'Den Arbeitsbereich vom Anfang des Levels neu aufbauen? Deine Änderungen hier gehen verloren.',
  workspaceReset: 'Arbeitsbereich zurückgesetzt.',
  workRestored: 'Deine Arbeit an diesem Level ist wieder da, wo du aufgehört hast. „Zurücksetzen“ fängt es neu an.',
  workLevelChanged: 'Dieses Level wurde geändert, seit du zuletzt daran gearbeitet hast, deshalb beginnt es neu.',
  levelLabel: (level) => `Level ${level}`,
  couldNotOpen: 'Dieses Level konnte nicht geöffnet werden.',
  tryAgain: 'Erneut versuchen',
  allLevels: 'Alle Level',
  preparing: 'Dein Arbeitsbereich wird vorbereitet.',
  opening: {
    barLabel: 'Das Level wird geöffnet',
    steps: {
      clearing: 'Arbeitsbereich wird geleert',
      unpacking: 'Level wird entpackt',
      starting: 'Was das Level braucht, wird gestartet',
      reading: 'Repository wird gelesen',
    },
  },
  help: 'Hilfe',
  unverified: (reasons) =>
    `Geschafft, aber das sah unplausibel aus: ${reasons.join('; ')}. Spiel das Level noch einmal, um es zu bestätigen.`,
  stillNotPassing: 'Klappt immer noch nicht? Lass es vorerst liegen – es behält seinen Platz auf der Karte, und nichts hindert dich daran zurückzukommen.',
  skipLevel: 'Dieses Level überspringen',
  whatJustHappened: 'Was gerade passiert ist',
  readDebrief: 'Die ganze Nachbesprechung lesen',
  nextWorld: (world) => `Weiter · ${world}`,
  straightToLevel: (level) => `Direkt zu Level ${level}`,
  nextLevel: (level) => `Weiter · Level ${level}`,
  hud: {
    brandTitle: 'Zurück zur Levelliste',
    noRepo: 'kein Repository',
    noRepoYet: 'noch kein Repository',
    detachedAt: (hash) => `detached bei ${hash}`,
    unbornBranch: 'Branch ohne Commits',
    clean: 'sauber',
    conflicted: (n) => `${n} mit Konflikt`,
    treeStatus: (staged, modified, untracked) => {
      const bits: string[] = [];
      if (staged) bits.push(`${staged} gestagt`);
      if (modified) bits.push(`${modified} geändert`);
      if (untracked) bits.push(`${untracked} ungetrackt`);
      return bits.join(' · ');
    },
    sandboxWorld: (world) => `Sandbox · ${world}`,
    warmup: 'Aufwärmen',
    warmupTitle: 'Die Werkzeuge, die dieses Level braucht, in zwei Minuten',
    reset: 'Zurücksetzen',
    resetTitle: 'Den Arbeitsbereich vom Level-Checkpoint neu aufbauen',
    checkTitle: 'Den Zustand des Repositorys bewerten (Strg+Enter)',
    checking: 'Wird geprüft …',
    check: 'Meine Arbeit prüfen',
  },
  frame: {
    graph: 'Git-Graph',
    reflog: 'Reflog',
    resizeTerminal: 'Größe des Terminal-Bereichs ändern',
    resizeTicket: 'Größe des Ticket-Bereichs ändern',
  },
  phone: {
    tabs: 'Bereiche des Levels',
    task: 'Aufgabe',
    terminal: 'Terminal',
    graph: 'Git-Graph',
    iris: 'Iris',
    keys: 'Tasten, die einer Handy-Tastatur fehlen',
    tab: 'Tab: Namen vervollständigen',
    ctrlC: 'Strg-C: Laufendes anhalten',
    esc: 'Esc-Taste',
    up: 'Vorheriger Befehl',
    down: 'Nächster Befehl',
    left: 'Cursor nach links',
    right: 'Cursor nach rechts',
  },
  brief: {
    objectives: 'Ziele',
    documentTitle: (level, title) => `Level ${level} · ${title} · EngineerOS`,
    anyTool: (model, modelId, date, runs) =>
      `Nimm hier jedes Werkzeug, das du willst, KI eingeschlossen: Bewertet wird das Repository, das du hinterlässt. Am ${date} hat ${model} (${modelId}), allein arbeitend, diesen Checkpoint ${num(runs, 'de')}-mal bekommen und kein einziges Mal bestanden.`,
    anyToolLevel:
      'Nimm jedes Werkzeug, das du willst, KI eingeschlossen: Frag deinen Agenten oder jeden anderen Assistenten, den du nutzt, was der Code tut. Bewertet wird das Repository, das du hinterlässt, nicht der Weg dorthin.',
    anyToolNoRepo:
      'Nimm jedes Werkzeug, das du willst, KI eingeschlossen: Lass dir von deinem Agenten oder jedem anderen Assistenten, den du nutzt, erklären, was du siehst. Bewertet werden die Dateien, die du hinterlässt, nicht der Weg dorthin.',
  },
  objectives: {
    hiddenCheck: (title) => `Verdeckte Prüfung: ${title}`,
  },
  work: {
    tablist: 'Arbeitsbereich',
    terminal: 'Terminal',
    editor: 'Editor',
    pulls: 'Pull Requests',
    connecting: 'verbinde …',
    noFile: 'keine Datei geöffnet',
    fileState: (path, dirty) => [path, ' · ', dirty ? 'nicht gespeichert' : 'gespeichert'],
    pullCount: (n) => (n === 0 ? 'keine Pull Requests' : `${n} ${n === 1 ? 'Pull Request' : 'Pull Requests'}`),
    pullsReading: 'wird gelesen …',
    split: 'Nebeneinander',
    splitTitle: 'Das Terminal neben dem Editor zeigen',
    resizeSplit: 'Größe von Editor und Terminal ändern',
  },
  files: {
    head: 'Dateien',
    treeLabel: 'Dateien im Arbeitsbereich',
    unsavedChanges: 'ungespeicherte Änderungen',
    reading: 'Der Arbeitsbereich wird gelesen …',
    empty: 'Hier gibt es noch keine Dateien. Leg eine im Terminal an.',
    unavailable: (error) => [
      'Die Dateiliste ist nicht verfügbar. ',
      error,
      '. Das Terminal funktioniert weiter, und dieser Bereich erholt sich beim nächsten Aktualisieren.',
    ],
    listFailed: 'der Arbeitsbereich konnte nicht aufgelistet werden',
    rootFallback: 'workspace',
  },
  editor: {
    blankLabel: 'Editor',
    opening: 'Wird geöffnet …',
    blankHint: 'Wähl links eine Datei, um sie hier zu bearbeiten. Strg+S speichert sie in den Arbeitsbereich, in dem das Terminal steht.',
    save: 'Speichern',
    saving: 'Wird gespeichert …',
    saveTitle: 'Speichern (Strg+S)',
    unsaved: 'nicht gespeichert',
    saved: 'gespeichert',
    noFile: 'keine Datei geöffnet',
    confirmDiscard: (open, next) => `${open} hat ungespeicherte Änderungen. ${next} öffnen und sie verwerfen?`,
    openFailed: 'die Datei konnte nicht geöffnet werden',
    saveFailed: 'die Datei konnte nicht gespeichert werden',
    run: 'Ausführen',
    runTests: 'Tests ausführen',
    runTitle: (command) => `Speichern, dann ${command} ins Terminal tippen (F5)`,
    runTestsTitle: (command) => `Speichern, dann ${command} ins Terminal tippen`,
    runBusy: 'Im Terminal läuft noch etwas. Stopp es dort mit Strg+C und führ es dann erneut aus.',
    runDetached: 'Das Terminal ist nicht verbunden, also gibt es keinen Ort, an dem das laufen könnte.',
    checkedDisk: (path) =>
      `${path} wurde außerhalb des Editors geändert, deshalb hat „Meine Arbeit prüfen“ die Version auf der Festplatte bewertet. Was hier steht, ist weiterhin nicht gespeichert.`,
  },
  pulls: {
    label: 'Pull Requests',
    reading: 'Das Repository wird gelesen …',
    emptyTitle: 'Keine Pull Requests',
    emptyNote: 'Die meisten Level haben keine. Wird einer in dem Repository geöffnet, in das dieser Arbeitsbereich pusht, erscheint er hier – mit seinen Checks, seinen Commits, den Dateien, die er berührt, und jedem Kommentar dazu.',
    unavailable: (error) => [
      'Die Pull Requests konnten nicht gelesen werden. ',
      error,
      '. Das Terminal funktioniert weiter, und dieser Bereich erholt sich beim nächsten Befehl.',
    ],
    readFailed: 'das Basis-Repository konnte nicht gelesen werden',
    state: {
      open: 'Offen',
      merged: 'Gemergt',
      closed: 'Geschlossen',
    },
    draft: 'Entwurf',
    openedBy: (author, date) => ['geöffnet von ', author, ' · ', date],
    mergesInto: (head, base) => `merget ${head} in ${base}`,
    checks: 'Checks',
    checkPass: 'bestanden',
    checkFail: 'fehlgeschlagen',
    checkPending: 'ausstehend',
    reviews: 'Reviews',
    reviewApproved: 'genehmigt',
    reviewChanges: 'Änderungen angefordert',
    reviewCommented: 'kommentiert',
    commits: 'Commits',
    files: 'Geänderte Dateien',
    comments: 'Kommentare',
    resolved: 'erledigt',
    onTheBranch: 'zum Branch',
  },
  graph: {
    label: 'Git-Commit-Graph',
    commitLabel: (short, subject) => `${short} ${subject}`,
    noRepo: 'Noch kein Git-Repository im Missionsverzeichnis.',
    noCommits: 'Das Repository hat noch keine Commits.',
    unreachable: 'unerreichbar',
  },
  commit: {
    keyCommit: 'commit',
    keySubject: 'Betreff',
    keyAuthor: 'Autor',
    keyParents: 'Vorgänger',
    keyRefs: 'Refs',
    authorAndDate: (author, date) => [author, ' · ', date],
    noParents: 'keine (Root-Commit)',
    refsReachable: 'keine direkt; von einem Branch aus erreichbar',
    refsUnreachable: 'keine. Kein Branch erreicht diesen Commit, aber es gibt ihn noch.',
  },
  reflog: {
    empty: 'Noch keine Reflog-Einträge.',
  },
};

export const ja: typeof en = {
  noRepoYet: 'まだ描くものがありません。ここにリポジトリができた時点で表示されます。',
  stillClear: 'クリアのままです。',
  checkingWork: '作業をチェックしています…',
  checkTakingLonger: 'チェックに通常より時間がかかっています。コマンドがタイムアウトするとチェックを終了します。作業内容は保持されます。',
  checkIncomplete: '結果が出る前にチェックがタイムアウトしました。実行し続けているコマンドを確認してから再試行してください。作業内容は保持されます。',
  retryCheck: 'チェックを再試行',
  objectivesToGo: (n: number) => `あと${n}個の目標。それぞれ、何が間違っているかは示しますが、直し方は示しません。`,
  confirmLastHint: 'このヒントは答えそのものです。それでも表示しますか？',
  confirmSkip: 'このレベルはいったん離れますか？星なしのままマップに残り、いつでも戻ってこられます。',
  confirmReset: 'レベルの最初からワークスペースを作り直しますか？ここでの変更は失われます。',
  workspaceReset: 'ワークスペースをリセットしました。',
  workRestored: 'このレベルでの作業は、中断したところに戻りました。「リセット」を押すと、最初からやり直せます。',
  workLevelChanged: 'このレベルは前回の作業のあとに更新されたため、最初から始まります。',
  levelLabel: (level: number) => `レベル${level}`,
  couldNotOpen: 'このレベルを開けませんでした。',
  tryAgain: 'もう一度試す',
  allLevels: 'すべてのレベル',
  preparing: 'ワークスペースを準備しています。',
  opening: {
    barLabel: 'レベルを開いています',
    steps: {
      clearing: 'ワークスペースをクリア中',
      unpacking: 'レベルを展開中',
      starting: 'レベルに必要なものを起動中',
      reading: 'リポジトリを読み込み中',
    },
  },
  help: 'ヘルプ',
  unverified: (reasons: string[]) => `クリアしましたが、不自然な点がありました: ${reasons.join('、')}。検証するには、レベルをもう一度プレイしてください。`,
  stillNotPassing: 'まだ合格しませんか？いったん離れても大丈夫です。マップ上の場所はそのまま残り、いつでも戻ってこられます。',
  skipLevel: 'このレベルをスキップ',
  whatJustHappened: '今起きたこと',
  readDebrief: '振り返りを全文読む',
  nextWorld: (world: string) => `次へ · ${world}`,
  straightToLevel: (level: number) => `レベル${level}へ直行`,
  nextLevel: (level: number) => `次へ · レベル${level}`,
  hud: {
    brandTitle: 'レベル一覧に戻る',
    noRepo: 'リポジトリなし',
    noRepoYet: 'リポジトリはまだありません',
    detachedAt: (hash: string) => `${hash}でdetached`,
    unbornBranch: 'コミットのないブランチ',
    clean: 'クリーン',
    conflicted: (n: number) => `コンフリクト${n}件`,
    treeStatus: (staged: number, modified: number, untracked: number) => {
      const bits: string[] = [];
      if (staged) bits.push(`ステージ済み${staged}`);
      if (modified) bits.push(`変更${modified}`);
      if (untracked) bits.push(`未追跡${untracked}`);
      return bits.join(' · ');
    },
    sandboxWorld: (world: string) => `サンドボックス · ${world}`,
    warmup: 'ウォームアップ',
    warmupTitle: 'このレベルに必要なツールを、2分で',
    reset: 'リセット',
    resetTitle: 'レベルのチェックポイントからワークスペースを作り直す',
    checkTitle: 'リポジトリの状態を採点(Ctrl+Enter)',
    checking: 'チェック中…',
    check: '作業をチェック',
  },
  frame: {
    graph: 'Gitグラフ',
    reflog: 'Reflog',
    resizeTerminal: 'ターミナルパネルのサイズを変更',
    resizeTicket: 'チケットパネルのサイズを変更',
  },
  phone: {
    tabs: 'レベルのパネル',
    task: 'タスク',
    terminal: 'ターミナル',
    graph: 'グラフ',
    iris: 'Iris',
    keys: 'スマホのキーボードにないキー',
    tab: 'Tab：名前を補完',
    ctrlC: 'Ctrl-C：実行中の処理を止める',
    esc: 'Escキー',
    up: '前のコマンド',
    down: '次のコマンド',
    left: 'カーソルを左へ',
    right: 'カーソルを右へ',
  },
  brief: {
    objectives: '目標',
    documentTitle: (level: number, title: string) => `レベル${level} · ${title} · EngineerOS`,
    anyTool: (model: string, modelId: string, date: string, runs: number) =>
      `ここではAIを含め、好きなツールを使って構いません。採点されるのは、あなたが残したリポジトリです。${date}に、${model}(${modelId})が単独でこのチェックポイントに${num(runs, 'ja')}回挑戦しましたが、一度も合格しませんでした。`,
    anyToolLevel: 'AIを含め、好きなツールを使って構いません。コードが何をしているのか、あなたのエージェントや、ほかに使っているアシスタントに聞いてみてください。採点されるのは、あなたが残したリポジトリであり、そこに至るまでの過程ではありません。',
    anyToolNoRepo: 'AIを含め、好きなツールを使って構いません。目の前にあるものの意味を、あなたのエージェントや、ほかに使っているアシスタントに説明してもらいましょう。採点されるのは、あなたが残したファイルであり、そこに至るまでの過程ではありません。',
  },
  objectives: {
    hiddenCheck: (title: string) => `非公開のチェック: ${title}`,
  },
  work: {
    tablist: '作業エリア',
    terminal: 'ターミナル',
    editor: 'エディター',
    pulls: 'プルリクエスト',
    connecting: '接続中…',
    noFile: '開いているファイルなし',
    fileState: (path: ReactNode, dirty: boolean) => [path, ' · ', dirty ? '未保存' : '保存済み'],
    pullCount: (n: number) => (n === 0 ? 'プルリクエストなし' : `プルリクエスト${n}件`),
    pullsReading: '読み込み中…',
    split: '左右に並べる',
    splitTitle: 'エディターの横にターミナルを表示',
    resizeSplit: 'エディターとターミナルのサイズを変更',
  },
  files: {
    head: 'ファイル',
    treeLabel: 'ワークスペースのファイル',
    unsavedChanges: '未保存の変更あり',
    reading: 'ワークスペースを読み込んでいます…',
    empty: 'まだファイルがありません。ターミナルで作成してください。',
    unavailable: (error: ReactNode): ReactNode[] => [
      'ファイル一覧を表示できません。',
      error,
      '。ターミナルは引き続き使えます。このパネルは次の更新で回復します。',
    ],
    listFailed: 'ワークスペースの一覧を取得できませんでした',
    rootFallback: 'workspace',
  },
  editor: {
    blankLabel: 'エディター',
    opening: '開いています…',
    blankHint: '左でファイルを選ぶと、ここで編集できます。Ctrl+Sで、ターミナルが開いているワークスペースに保存されます。',
    save: '保存',
    saving: '保存中…',
    saveTitle: '保存(Ctrl+S)',
    unsaved: '未保存',
    saved: '保存済み',
    noFile: '開いているファイルなし',
    confirmDiscard: (open: string, next: string) => `${open}には未保存の変更があります。${next}を開くと、その変更は失われます。開きますか？`,
    openFailed: 'ファイルを開けませんでした',
    saveFailed: 'ファイルを保存できませんでした',
    run: '実行',
    runTests: 'テストを実行',
    runTitle: (command: string) => `保存してから、ターミナルに${command}と入力します(F5)`,
    runTestsTitle: (command: string) => `保存してから、ターミナルに${command}と入力します`,
    runBusy: 'ターミナルでまだ何かが実行中です。ターミナルでCtrl+Cを押して止めてから、もう一度実行してください。',
    runDetached: 'ターミナルが接続されていないため、実行できる場所がありません。',
    checkedDisk: (path: string) => `${path}がエディターの外で変更されたため、「作業をチェック」はディスク上のバージョンを採点しました。ここにある内容は、まだ保存されていません。`,
  },
  pulls: {
    label: 'プルリクエスト',
    reading: 'リポジトリを読み込んでいます…',
    emptyTitle: 'プルリクエストはありません',
    emptyNote: 'ほとんどのレベルにはありません。このワークスペースがプッシュ先にしているリポジトリでプルリクエストが開かれると、ここに表示されます。チェック、コミット、変更されるファイル、付けられたコメントもすべて確認できます。',
    unavailable: (error: ReactNode): ReactNode[] => [
      'プルリクエストを読み込めませんでした。',
      error,
      '。ターミナルは引き続き使えます。このパネルは次のコマンドで回復します。',
    ],
    readFailed: 'ベースのリポジトリを読み込めませんでした',
    state: {
      open: 'オープン',
      merged: 'マージ済み',
      closed: 'クローズ',
    },
    draft: 'ドラフト',
    openedBy: (author: ReactNode, date: string): ReactNode[] => [author, 'が作成 · ', date],
    mergesInto: (head: string, base: string) => `${head}を${base}にマージします`,
    checks: 'チェック',
    checkPass: '成功',
    checkFail: '失敗',
    checkPending: '待機中',
    reviews: 'レビュー',
    reviewApproved: '承認',
    reviewChanges: '変更をリクエスト',
    reviewCommented: 'コメント',
    commits: 'コミット',
    files: '変更されたファイル',
    comments: 'コメント',
    resolved: '解決済み',
    onTheBranch: 'ブランチ全体へ',
  },
  graph: {
    label: 'Gitのコミットグラフ',
    commitLabel: (short: string, subject: string) => `${short} ${subject}`,
    noRepo: 'ミッションのディレクトリには、まだGitリポジトリがありません。',
    noCommits: 'リポジトリにはまだコミットがありません。',
    unreachable: '到達不能',
  },
  commit: {
    keyCommit: 'commit',
    keySubject: '件名',
    keyAuthor: '作成者',
    keyParents: '親',
    keyRefs: 'refs',
    authorAndDate: (author: ReactNode, date: string): ReactNode[] => [author, ' · ', date],
    noParents: 'なし(ルートコミット)',
    refsReachable: '直接のものはなし。ブランチから到達できます',
    refsUnreachable: 'なし。どのブランチもこのコミットに到達できませんが、コミット自体はまだ存在します。',
  },
  reflog: {
    empty: 'Reflogのエントリはまだありません。',
  },
};
